import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import type { AccessBlock } from '@shared/entitlementAccess';
import { FEATURE_FLAGS } from '@shared/featureFlags';
import type { RoleId } from '@shared/roles';
import {
  DEFAULT_TEAM_POLICY,
  normalizeTeamPolicy,
  type TeamPolicy,
} from '@shared/teamPolicy';
import { fetchFreshIdToken } from '@/auth/freshIdToken';
import { isPreviewSessionActive } from '@/auth/previewMode';
import { ROLES } from '@/config/roles';
import { getBehaviorConfig } from '@/config/runtimeBrandConfig';
import { featureFlagsApi, userApi } from '@/services/api';
import { registerAuthBridge } from '@/services/authBridge';
import type { DemoViewState } from '@/services/userApi';
import { useServerStatus } from '@/store/serverStatusStore';
import { getDefaultRedirectPath, isValidRole } from '@/utils/accessControl';
import {
  isDevLoadingTraceEnabled,
  useDevLoadingTrace,
} from '@/utils/devLoadingTrace';

import type { AuthContextValue, EnhancedUser } from './authContext';
import { AuthContext } from './authContext';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Read the role payload's sample-student block, or null when the server said
 * nothing usable — an older server, a proxy error page, or a partially-shaped
 * body must all read as "no switch offered" rather than as an available one.
 * `available` and `active` therefore have to be real booleans.
 *
 * `actor` is normalized rather than enforced: it only names who is really
 * signed in, so a missing name must not cost a flagged teacher the switch.
 */
const parseDemoView = (raw: unknown): DemoViewState | null => {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;
  if (typeof candidate.available !== 'boolean') return null;
  if (typeof candidate.active !== 'boolean') return null;

  let persona: DemoViewState['persona'] = null;
  if (candidate.persona != null) {
    if (typeof candidate.persona !== 'object') return null;
    const personaName = (candidate.persona as Record<string, unknown>)
      .displayName;
    if (typeof personaName !== 'string') return null;
    persona = { displayName: personaName };
  }

  const rawActor =
    candidate.actor && typeof candidate.actor === 'object'
      ? (candidate.actor as Record<string, unknown>)
      : {};
  const actorName = rawActor.displayName;

  return {
    available: candidate.available,
    active: candidate.active,
    persona,
    actor: { displayName: typeof actorName === 'string' ? actorName : null },
  };
};

type AuthBootstrapPhase =
  | 'initializing'
  | 'seeding-feature-defaults'
  | 'waiting-auth0'
  | 'preview'
  | 'logged-out-defaults'
  | 'waiting-user-profile'
  | 'server-down-defaults'
  | 'fetching-role'
  | 'fetching-feature-flags'
  | 'loaded'
  | 'error';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user,
    isAuthenticated,
    isLoading: isAuth0Loading,
    getIdTokenClaims,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();
  const [userRole, setUserRole] = useState<RoleId>(ROLES.USER);
  const [isRoleLoaded, setIsRoleLoaded] = useState(false);
  // Neutral until the server tells us. NOT 'inactive' — a default of
  // 'inactive' lets a protected route bounce an authenticating/active user to
  // /pending-approval the moment the role is (briefly) marked loaded. Only an
  // explicit server status should ever route there.
  const [userStatus, setUserStatus] = useState('unknown');
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [teamPolicy, setTeamPolicy] = useState<TeamPolicy>(DEFAULT_TEAM_POLICY);
  // Onboarding gate: server-computed on the role payload. Default false so a
  // transient fetch failure can never lock a student out of practice.
  const [owesDiagnostic, setOwesDiagnostic] = useState(false);
  // Access gate: server-computed on the role payload (e.g. expired trial).
  // Default null so a transient fetch failure never blocks a valid student.
  const [accessBlock, setAccessBlock] = useState<AccessBlock | null>(null);
  // Sample-student view: server-computed on the role payload. Null means the
  // server offered nothing usable, which is also the right answer for every
  // identity that cannot switch.
  const [demoView, setDemoView] = useState<DemoViewState | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [featureFlagsMap, setFeatureFlagsMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const [authBootstrapPhase, setAuthBootstrapPhase] =
    useState<AuthBootstrapPhase>('initializing');
  const [roleFetchActive, setRoleFetchActive] = useState(false);
  const [featureFlagFetchActive, setFeatureFlagFetchActive] = useState(false);
  const isServerDown = useServerStatus();

  // Read the per-client preview gate, not the raw env var: a human dev can
  // set `dt-skip-preview` to opt this browser out of preview mode without
  // disabling it for the whole dev server (and the AI agents on it).
  const isPreviewMode = isPreviewSessionActive();
  const setTracePhase = useCallback((phase: AuthBootstrapPhase) => {
    if (isDevLoadingTraceEnabled()) setAuthBootstrapPhase(phase);
  }, []);
  const setRoleTraceActive = useCallback((active: boolean) => {
    if (isDevLoadingTraceEnabled()) setRoleFetchActive(active);
  }, []);
  const setFeatureFlagTraceActive = useCallback((active: boolean) => {
    if (isDevLoadingTraceEnabled()) setFeatureFlagFetchActive(active);
  }, []);

  useDevLoadingTrace('auth.auth0-loading', isAuth0Loading && !isPreviewMode, {
    isAuth0Loading,
    isPreviewMode,
    isAuthenticated,
    hasAuth0User: Boolean(user?.sub),
    isRoleLoaded,
    authBootstrapPhase,
  });

  useDevLoadingTrace('auth.role-loaded-gate', !isRoleLoaded, {
    roleLoading: !isRoleLoaded,
    isAuth0Loading,
    isPreviewMode,
    isAuthenticated,
    hasAuth0User: Boolean(user?.sub),
    isServerDown,
    authBootstrapPhase,
    roleFetchActive,
    featureFlagFetchActive,
  });

  useDevLoadingTrace('auth.role-fetch', roleFetchActive, {
    roleFetchActive,
    isAuthenticated,
    hasAuth0User: Boolean(user?.sub),
    isServerDown,
    authBootstrapPhase,
  });

  useDevLoadingTrace('auth.feature-flags-fetch', featureFlagFetchActive, {
    featureFlagFetchActive,
    featureFlagsCount: featureFlagsMap.size,
    authBootstrapPhase,
  });

  const fetchUserRole = useCallback(async () => {
    setTracePhase('seeding-feature-defaults');
    try {
      const defaults = getBehaviorConfig()?.featureFlags || {};
      const defaultsMap = new Map(
        Object.entries(defaults).map(
          ([id, enabled]) => [id, !!enabled] as const
        )
      );
      // Seed brand defaults ONLY while nothing is loaded yet (cold boot).
      // On a refetch (e.g. refetchRole after the placement diagnostic
      // completes) the map already holds the user's real flags; resetting it
      // to defaults here would transiently revoke feature-gated routes for
      // the two network round-trips below — EnhancedProtectedRoute would
      // evaluate the default map and bounce the user off a still-gated page.
      setFeatureFlagsMap(prev => (prev.size > 0 ? prev : defaultsMap));
    } catch {
      // ignore seeding errors; proceed
    }

    if (isPreviewMode) {
      setRoleTraceActive(false);
      setFeatureFlagTraceActive(false);
      setTracePhase('preview');
      // Role is parameterized — AI-agent flows default to super_admin, E2E to user.
      // Must match `PREVIEW_MODE_ROLE` on the backend for a coherent session.
      const previewRole =
        (import.meta.env.VITE_PREVIEW_MODE_ROLE as RoleId) || ROLES.SUPER_ADMIN;
      setUserRole(isValidRole(previewRole) ? previewRole : ROLES.SUPER_ADMIN);
      // Mark the preview session active so EnhancedProtectedRoute doesn't
      // redirect to /pending-approval (default status is the neutral 'unknown').
      setUserStatus('active');
      setFeatureFlagsMap(
        new Map([[FEATURE_FLAGS.ADMIN_FEATURE_MANAGEMENT.id, true]])
      );
      setTeamPolicy(DEFAULT_TEAM_POLICY);
      setDemoView(null);
      setIsRoleLoaded(true);
      return;
    }

    // Wait for Auth0 to finish silent re-auth before deciding the role.
    // Otherwise we'd briefly publish unauthenticated defaults (`userRole=user`)
    // with `isRoleLoaded=true`, and protected routes would redirect on stale
    // state the moment auth flips authenticated.
    if (isAuth0Loading) {
      setRoleTraceActive(false);
      setFeatureFlagTraceActive(false);
      setTracePhase('waiting-auth0');
      return;
    }

    if (!isAuthenticated) {
      setRoleTraceActive(false);
      setFeatureFlagTraceActive(false);
      setTracePhase('logged-out-defaults');
      // Genuinely logged out — settle with defaults so guards can route to login.
      setUserRole(ROLES.USER);
      setTeamPolicy(DEFAULT_TEAM_POLICY);
      setDemoView(null);
      setIsRoleLoaded(true);
      return;
    }

    if (!user?.sub) {
      setRoleTraceActive(false);
      setFeatureFlagTraceActive(false);
      setTracePhase('waiting-user-profile');
      // Authenticated but Auth0 hasn't hydrated the profile yet — stay loading
      // (don't mark role loaded) so a protected route can't redirect on the
      // pre-fetch default. The effect re-runs when `user` arrives.
      return;
    }

    if (isServerDown) {
      setRoleTraceActive(false);
      setFeatureFlagTraceActive(false);
      setTracePhase('server-down-defaults');
      setUserRole(ROLES.USER);
      setTeamPolicy(DEFAULT_TEAM_POLICY);
      setDemoView(null);
      setIsRoleLoaded(true);
      return;
    }

    try {
      setAuthError(null);
      setRoleTraceActive(true);
      setTracePhase('fetching-role');

      const token = await fetchFreshIdToken({
        getIdTokenClaims,
        getAccessTokenSilently,
      });
      if (!token) throw new Error('Not authenticated');

      const data = await userApi.getUserRole(user.sub, user.email ?? '', token);
      setRoleTraceActive(false);
      const role = data.role || ROLES.USER;
      const status = data.userStatus || 'inactive';
      const resolvedTeamPolicy = normalizeTeamPolicy(
        data.teamPolicy as Partial<TeamPolicy> | null | undefined
      );

      if (isValidRole(role)) {
        setUserRole(role as RoleId);
      } else {
        console.warn(`Invalid role received: ${role}, defaulting to user role`);
        setUserRole(ROLES.USER);
      }
      setUserStatus(status);
      setDisplayName(data.displayName || null);
      setTeamPolicy(resolvedTeamPolicy);
      setOwesDiagnostic(data.owesDiagnostic === true);
      setAccessBlock(data.accessBlock ?? null);
      setDemoView(parseDemoView(data.demoView));

      try {
        setFeatureFlagTraceActive(true);
        setTracePhase('fetching-feature-flags');
        const myFeatures = await featureFlagsApi.getMyFeatureFlags(token);
        const map = new Map(
          (myFeatures.features || [])
            .filter(
              (f: { id: string; enabled: unknown }) =>
                typeof f.enabled === 'boolean'
            )
            .map(
              (f: { id: string; enabled: boolean }) =>
                [f.id, f.enabled] as const
            )
        );
        setFeatureFlagsMap(map);
      } catch (ffErr) {
        console.warn(
          'Failed to load feature flags, falling back to brand defaults:',
          ffErr
        );
        const defaults = getBehaviorConfig()?.featureFlags || {};
        const defaultsMap = new Map(
          Object.entries(defaults).map(
            ([id, enabled]) => [id, !!enabled] as const
          )
        );
        // Same do-not-downgrade contract as the role-fetch error path: on a
        // transient flags-fetch failure during a REFETCH, keep the last-known
        // per-user flags instead of demoting to brand defaults mid-session.
        setFeatureFlagsMap(prev => (prev.size > 0 ? prev : defaultsMap));
      } finally {
        setFeatureFlagTraceActive(false);
      }
      setTracePhase('loaded');
    } catch (error) {
      setRoleTraceActive(false);
      setFeatureFlagTraceActive(false);
      setTracePhase('error');
      console.error('Error fetching user role:', error);
      setAuthError(
        (error instanceof Error ? error.message : null) ||
          'Failed to fetch user role'
      );
      // Do NOT downgrade role/status on a transient fetch failure (e.g. the dev
      // backend restarting on a code change). Publishing 'inactive' here is what
      // yanked an active user to /pending-approval on every rerender. Keep the
      // last-known values — the server remains the source of truth per request.
    } finally {
      setIsRoleLoaded(true);
    }
  }, [
    user,
    isServerDown,
    isAuthenticated,
    isAuth0Loading,
    getIdTokenClaims,
    getAccessTokenSilently,
    isPreviewMode,
    setFeatureFlagTraceActive,
    setRoleTraceActive,
    setTracePhase,
  ]);

  const updateUserRole = useCallback((newRole: RoleId) => {
    if (isValidRole(newRole)) {
      setUserRole(newRole);
    } else {
      console.warn(`Attempted to set invalid role: ${newRole}`);
    }
  }, []);

  const loginWithPath = useCallback(
    (returnTo?: string) => {
      // Without a returnTo, /callback resolves the role default post-login.
      loginWithRedirect(returnTo ? { appState: { returnTo } } : undefined);
    },
    [loginWithRedirect]
  );

  const logoutUser = useCallback(() => {
    setUserRole(ROLES.USER);
    setIsRoleLoaded(false);
    setAuthError(null);
    setTeamPolicy(DEFAULT_TEAM_POLICY);
    setDemoView(null);
    logout({ returnTo: window.location.origin } as never);
  }, [logout]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }

    const token = await fetchFreshIdToken({
      getIdTokenClaims,
      getAccessTokenSilently,
    });
    if (!token) throw new Error('User not authenticated');
    return token;
  }, [isAuthenticated, getIdTokenClaims, getAccessTokenSilently]);

  const getRedirectPath = useCallback(() => {
    return getDefaultRedirectPath(userRole, {
      role: userRole,
      teamPolicy,
      featureFlags: featureFlagsMap,
    });
  }, [featureFlagsMap, teamPolicy, userRole]);

  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  // Hand the plain API layer (`fetchWithConfig`) the ability to recover from a
  // 401 on ANY authenticated surface: force-refresh the ID token, and — if the
  // session is dead — redirect to login preserving the current path. Skipped in
  // preview mode (the backend short-circuits auth, so no 401s and no real login
  // flow). See `src/services/authBridge.ts`.
  useEffect(() => {
    if (isPreviewMode) return;
    registerAuthBridge({
      refresh: () =>
        fetchFreshIdToken(
          { getIdTokenClaims, getAccessTokenSilently },
          { forceRefresh: true }
        ),
      redirect: returnTo => {
        loginWithRedirect({ appState: { returnTo } }).catch(() => {
          // loginWithRedirect can throw on Auth0 init failure — hard-navigate so
          // the user isn't stranded on a dead session.
          if (typeof window !== 'undefined') window.location.href = '/';
        });
      },
    });
  }, [
    isPreviewMode,
    getIdTokenClaims,
    getAccessTokenSilently,
    loginWithRedirect,
  ]);

  const enhancedUser: EnhancedUser | null = isPreviewMode
    ? {
        sub: 'preview-user-id',
        email: 'preview@example.com',
        role: userRole,
        userStatus,
        teamPolicy,
        featureFlags: featureFlagsMap,
        owesDiagnostic: false,
        accessBlock: null,
      }
    : user
      ? {
          ...user,
          sub: user.sub!,
          role: userRole,
          userStatus,
          displayName,
          teamPolicy,
          featureFlags: featureFlagsMap,
          owesDiagnostic,
          accessBlock,
        }
      : null;

  const value: AuthContextValue = {
    user: enhancedUser,
    isAuthenticated: isAuthenticated || isPreviewMode,
    // Preview mode is fully synchronous (mocked context returns isLoading=false),
    // so guard the SDK loading flag against it for clarity.
    isAuthLoading: isAuth0Loading && !isPreviewMode,
    userRole,
    userStatus,
    teamPolicy,
    isRoleLoaded,
    authError,
    isServerDown,
    demoView,
    updateUserRole,
    loginWithPath,
    logoutUser,
    getRedirectPath,
    getAccessToken,
    refetchRole: fetchUserRole,
    setUserStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
